import useWindowSize from "@shared/hooks/useWindowSize";
import breakpoints from "@shared/theme/props/breakpoints";
import { useTranslation } from "@shared/utils/internationalization";
import Drawer from "@ui-kit/overlays/Drawer/Drawer.web";
import Modal from "@ui-kit/overlays/Modal/Modal.web";
import BestAvailableQuantityInput from "../BestAvailableQuantityInput/BestAvailableQuantityInput.web";
import { BestAvailableQuantitySelectProps } from "./BestAvailableQuantitySelect.props";

export default function BestAvailableQuantitySelect({
  onClose,
  onSubmit,
  ...rest
}: BestAvailableQuantitySelectProps) {
  const { width } = useWindowSize();
  const isMobile = width && width < breakpoints.sm;

  const handleSubmit = (quantity: number) => {
    onSubmit(quantity);
    onClose();
  };

  return isMobile ? (
    <BestAvailableQuantitySelect.Drawer
      onClose={onClose}
      onSubmit={handleSubmit}
      {...rest}
    />
  ) : (
    <BestAvailableQuantitySelect.Modal
      onClose={onClose}
      onSubmit={handleSubmit}
      {...rest}
    />
  );
}

BestAvailableQuantitySelect.Modal = function QuantitySelectModal({
  isOpen,
  onClose,
  onSubmit,
  minQuantity,
  maxQuantity,
  currentQuantity,
}: BestAvailableQuantitySelectProps): JSX.Element {
  const { t } = useTranslation("seating");
  return (
    <Modal isOpen={isOpen} size="lg" onClose={onClose} isCentered>
      <Modal.Header>{t("How many tickets would you like?")}</Modal.Header>
      <Modal.Body>
        <BestAvailableQuantitySelect.Content
          minQuantity={minQuantity}
          maxQuantity={maxQuantity}
          onSubmit={onSubmit}
          currentQuantity={currentQuantity}
        />
      </Modal.Body>
    </Modal>
  );
};

BestAvailableQuantitySelect.Drawer = function QuantitySelectDrawer({
  isOpen,
  onClose,
  onSubmit,
  minQuantity,
  maxQuantity,
  currentQuantity,
}: BestAvailableQuantitySelectProps): JSX.Element {
  const { t } = useTranslation("seating");
  return (
    <Drawer
      isOpen={isOpen}
      onClose={onClose}
      placement="bottom"
      drawerContentProps={{
        maxHeight: "var(--100dvh)",
      }}
    >
      <Drawer.Header textAlign="center">
        {t("How many tickets would you like?")}
      </Drawer.Header>
      <Drawer.Body>
        <BestAvailableQuantitySelect.Content
          minQuantity={minQuantity}
          maxQuantity={maxQuantity}
          onSubmit={onSubmit}
          currentQuantity={currentQuantity}
        />
      </Drawer.Body>
      <Drawer.Footer />
    </Drawer>
  );
};

BestAvailableQuantitySelect.Content = function Content({
  minQuantity,
  maxQuantity,
  onSubmit,
  currentQuantity,
}: Omit<
  BestAvailableQuantitySelectProps,
  "onOpen" | "onClose" | "isOpen"
>): JSX.Element {
  return (
    <BestAvailableQuantityInput
      minimum={minQuantity}
      maximum={maxQuantity}
      value={currentQuantity ?? minQuantity}
      onChange={onSubmit}
    />
  );
};
